import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, isSupported, MessagePayload, Messaging, onMessage } from 'firebase/messaging'
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'

import { getCookie } from '@/shared/lib/utils/common/getCookie'
import { setCookie } from '@/shared/lib/utils/common/setCookie'

import { firebaseConfig, vapidKey } from '../config/firebase-config'
import { isIOS } from '../lib/is-ios'
import { isStandalone } from '../lib/is-standalone'

import { useWebPushToken } from './use-web-push-token'

type UseWebPushOptions = {
	onMessageHandler?: (data: MessagePayload['data']) => any
}

type WebPushContextValue = {
	messaging: Messaging | null
	notification: MessagePayload | null
	permission: NotificationPermission
	isLoading: boolean
	error: unknown
	subscribe: () => Promise<void>
	dontSubscribe: () => void
	init: (cancelled?: boolean) => Promise<void>
	registerDevice: (uuid: string, phone?: string | null) => Promise<void>
	getPushToken: () => string
	clearNotification: () => void
	hasToken: boolean
	isSupported: boolean | null
	isIOSDevice: boolean
	isPWA: boolean
	isDontSubscribe: boolean
}

const TEMPORARY_DONT_SUB_PUSH_KEY = 'temporary_dont_sub_push_key'

const WebPushContext = createContext<WebPushContextValue | null>(null)

// Функция для получения версии iOS
const getIOSVersion = (): number | null => {
	if (typeof window === 'undefined' || !isIOS()) return null

	const match = navigator.userAgent.match(/OS (\d+)_(\d+)/)
	if (match) {
		return parseFloat(`${match[1]}.${match[2]}`)
	}
	return null
}

const useProvideWebPush = ({ onMessageHandler }: UseWebPushOptions): WebPushContextValue => {
	const { token, getToken: getPushToken, saveToken, deleteToken, linkTokenWithUser } = useWebPushToken()

	const [permission, setPermission] = useState<NotificationPermission>('default')
	const [messaging, setMessaging] = useState<Messaging | null>(null)
	const [error, setError] = useState<unknown>(null)
	const [notification, setNotification] = useState<MessagePayload | null>(null)

	const [isSupportedBrowser, setIsSupportedBrowser] = useState<boolean | null>(null)
	const [isIOSDevice, setIsIOSDevice] = useState(false)
	const [isPWA, setIsPWA] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [isDontSubscribe, setDontSubscribe] = useState(true)

	useEffect(() => {
		const dontSub = getCookie(TEMPORARY_DONT_SUB_PUSH_KEY)

		setDontSubscribe(!!dontSub)
	}, [])

	useEffect(() => {
		let cancelled = false

		init(cancelled)

		return () => {
			cancelled = true
		}
	}, [])

	const registerServiceWorker = useCallback(async () => {
		try {
			const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
				scope: '/'
			})

			await navigator.serviceWorker.ready

			return registration
		} catch (swError) {
			console.error('Service Worker registration failed:', swError)
			setError(new Error('SW_REGISTRATION_FAILED'))
			return null
		}
	}, [])

	const initPushEvents = useCallback(
		(messaging: Messaging) => {
			onMessage(messaging, payload => {
				if (onMessageHandler) {
					onMessageHandler(payload.data)
				}
				setNotification(payload)
			})
		},
		[onMessageHandler]
	)

	const errorHandler = useCallback((e: any) => {
		if (e instanceof Error) {
			if (e.message.includes('messaging/unsupported-browser')) {
				setError(new Error('UNSUPPORTED_BROWSER'))
			} else if (e.message.includes('messaging/permission-blocked')) {
				setError(new Error('PERMISSION_BLOCKED'))
			} else {
				setError(e)
			}
		} else {
			setError(e)
		}
	}, [])

	const init = useCallback(
		async (cancelled: boolean = false) => {
			const supported = await isSupported()

			setIsSupportedBrowser(supported)
			const iosDevice = isIOS()
			setIsIOSDevice(iosDevice)
			setIsPWA(isStandalone())

			// Проверка версии iOS (веб-пуши поддерживаются только с iOS 16.4+)
			if (iosDevice) {
				const iosVersion = getIOSVersion()
				const isIOS16_4Plus = iosVersion !== null && iosVersion >= 16.4

				if (!isIOS16_4Plus) {
					setError(new Error('IOS_VERSION_TOO_OLD'))
					return
				}
			}

			setPermission(Notification?.permission || 'default')

			if (cancelled) return

			if (!supported) return

			// Для iOS важно зарегистрировать Service Worker ДО инициализации Firebase
			await registerServiceWorker()
			if (cancelled) return

			const app = initializeApp(firebaseConfig)
			const messagingInstance = getMessaging(app)

			initPushEvents(messagingInstance)

			if (!cancelled) setMessaging(messagingInstance)
		},
		[registerServiceWorker, initPushEvents]
	)

	const subscribe = useCallback(async () => {
		if (!messaging || isSupportedBrowser === false) return

		// Проверка версии iOS
		if (isIOSDevice) {
			const iosVersion = getIOSVersion()
			const isIOS16_4Plus = iosVersion !== null && iosVersion >= 16.4

			if (!isIOS16_4Plus) {
				setError(new Error('IOS_VERSION_TOO_OLD'))
				setIsLoading(false)
				return
			}

			if (!isPWA) {
				setError(new Error('iOS_REQUIRES_PWA'))
				setIsLoading(false)
				return
			}
		}

		try {
			setIsLoading(true)
			setError(null)

			// Убедиться, что Service Worker зарегистрирован перед запросом разрешения
			// Это критично для iOS
			let registration = await navigator.serviceWorker.ready

			if (!registration) {
				registration = await registerServiceWorker()
			}

			if (!registration) {
				setError(new Error('SW_REGISTRATION_FAILED'))
				setIsLoading(false)
				return
			}

			// Запрос разрешения после регистрации Service Worker
			const permission = await Notification.requestPermission()
			setPermission(permission)

			if (permission !== 'granted') {
				setError(new Error('PERMISSION_DENIED'))
				deleteToken()
				return
			}

			// Получение токена с явным указанием registration
			const newToken = await getToken(messaging, {
				vapidKey,
				serviceWorkerRegistration: registration
			})

			if (!newToken) {
				setError(new Error('TOKEN_GENERATION_FAILED'))
				return
			}

			saveToken(newToken)
		} catch (e) {
			errorHandler(e)
			deleteToken()
		} finally {
			setIsLoading(false)
		}
	}, [deleteToken, errorHandler, isIOSDevice, isPWA, isSupportedBrowser, messaging, registerServiceWorker, saveToken])

	const dontSubscribe = () => {
		const expires = new Date()
		expires.setDate(expires.getDate() + 3)

		setCookie(TEMPORARY_DONT_SUB_PUSH_KEY, 'true', {
			expires
		})
	}

	return {
		messaging,
		notification,
		permission,
		error,
		subscribe,
		dontSubscribe,
		init,
		registerDevice: linkTokenWithUser,
		getPushToken,
		clearNotification: () => setNotification(null),
		hasToken: !!token,
		isSupported: isSupportedBrowser,
		isLoading,
		isIOSDevice,
		isPWA,
		isDontSubscribe
	}
}

type WebPushProviderProps = UseWebPushOptions & {
	children: ReactNode
}

export const WebPushProvider = ({ children, ...options }: WebPushProviderProps) => {
	const value = useProvideWebPush(options)

	return <WebPushContext.Provider value={value}>{children}</WebPushContext.Provider>
}

export const useWebPush = () => {
	const context = useContext(WebPushContext)

	if (!context) {
		throw new Error('useWebPush must be used within WebPushProvider')
	}

	return context
}
